import { lazy } from 'react';
import { RouteObject } from 'react-router-dom';
import { AuthGuard } from '../components/feature/AuthGuard';
import { AdminAuthGuard } from '../components/feature/AdminAuthGuard';
import { AdminLocalGuard } from '../components/feature/AdminLocalGuard';

const HomePage = lazy(() => import('../pages/home/page'));
const AboutPage = lazy(() => import('../pages/about/page'));
const ContactPage = lazy(() => import('../pages/contact/page'));
const EquipmentPage = lazy(() => import('../pages/equipment/page'));
const EquipmentCategoryPage = lazy(() => import('../pages/equipment/category/page'));
const EquipmentDetailPage = lazy(() => import('../pages/equipment/detail/page'));
const LocationsPage = lazy(() => import('../pages/locations/page'));
const LocationDetailPage = lazy(() => import('../pages/locations/detail/page'));
const ServicesPage = lazy(() => import('../pages/services/page'));
const TaiwanJapanPage = lazy(() => import('../pages/services/taiwan-japan/page'));
const PostProductionPage = lazy(() => import('../pages/services/post-production/page'));
const UrentPage = lazy(() => import('../pages/services/urent/page'));
const LoginPage = lazy(() => import('../pages/account/login/page'));
const RegisterPage = lazy(() => import('../pages/account/register/page'));
const ForgotPasswordPage = lazy(() => import('../pages/account/forgot-password/page'));
const ResetPasswordPage = lazy(() => import('../pages/account/reset-password/page'));
const ConfirmEmailPage = lazy(() => import('../pages/account/confirm-email/page'));
const AuthCallbackPage = lazy(() => import('../pages/account/confirm-email/page'));
const ProfilePage = lazy(() => import('../pages/account/profile/page'));
const OrdersPage = lazy(() => import('../pages/account/orders/page'));
const OrderDetailPage = lazy(() => import('../pages/account/orders/detail/page'));
const CartPage = lazy(() => import('../pages/account/cart/page'));
const CheckoutPage = lazy(() => import('../pages/account/checkout/page'));
const CheckoutSuccessPage = lazy(() => import('../pages/account/checkout/success/page'));
const PrepaidApplyPage = lazy(() => import('../pages/account/prepaid/apply/page'));
const PrepaidStatusPage = lazy(() => import('../pages/account/prepaid/status/page'));
const PrepaidLedgerPage = lazy(() => import('../pages/account/prepaid/ledger/page'));
const UrentApplyPage = lazy(() => import('../pages/account/urent/apply/page'));
const UrentStatusPage = lazy(() => import('../pages/account/urent/status/page'));
const IdentityPage = lazy(() => import('../pages/account/identity/page'));
const NotFoundPage = lazy(() => import('../pages/NotFound'));

// Legal pages
const PrivacyPage = lazy(() => import('../pages/privacy/page'));
const TermsPage = lazy(() => import('../pages/terms/page'));
const LinePrivacyPage = lazy(() => import('../pages/line-privacy/page'));

// MuVi Space
const MuViSpacePage = lazy(() => import('../pages/muvi-space/page'));
const MuViSpaceBookPage = lazy(() => import('../pages/muvi-space/book/page'));

// Account notification settings
const AccountNotificationsPage = lazy(() => import('../pages/account/notifications/page'));

// Admin Pages
const AdminLoginPage = lazy(() => import('../pages/admin/login/page'));
const AdminRegisterPage = lazy(() => import('../pages/admin/register/page'));
const AdminInvitePage = lazy(() => import('../pages/admin/invite/page'));
const AdminDashboardPage = lazy(() => import('../pages/admin/dashboard/page'));
const AdminOrdersPage = lazy(() => import('../pages/admin/orders/page'));
const AdminOrderDetailPage = lazy(() => import('../pages/admin/orders/detail/page'));
const AdminOrderContractPage = lazy(() => import('../pages/admin/orders/contract/page'));
const AdminEquipmentPage = lazy(() => import('../pages/admin/equipment/page'));
const AdminEquipmentItemsPage = lazy(() => import('../pages/admin/equipment/items/page'));
const AdminLocationsPage = lazy(() => import('../pages/admin/locations/page'));
const AdminFormsPage = lazy(() => import('../pages/admin/forms/page'));
const AdminCMSPage = lazy(() => import('../pages/admin/cms/page'));
const AdminAnalyticsPage = lazy(() => import('../pages/admin/analytics/page'));
const AdminCategoriesPage = lazy(() => import('../pages/admin/categories/page'));
const BrandsPage = lazy(() => import('../pages/admin/brands/page'));
const AdminCustomersPage = lazy(() => import('../pages/admin/customers/page'));
const AdminAdminsPage = lazy(() => import('../pages/admin/admins/page'));
const AdminContractsPage = lazy(() => import('../pages/admin/contracts/page'));
const AdminUrentContractsPage = lazy(() => import('../pages/admin/urent-contracts/page'));
const AdminAccessoriesPage = lazy(() => import('../pages/admin/accessories/page'));
const AdminCalendarTestPage = lazy(() => import('../pages/admin/calendar-test/page'));
const GoogleCalendarSettingsPage = lazy(() => import('../pages/admin/settings/google-calendar/page'));
const GoogleCalendarCallbackPage = lazy(() => import('../pages/admin/settings/google-calendar/callback/page'));
const AdminUrentManagementPage = lazy(() => import('../pages/admin/urent/page'));
const AdminUrentLedgerPage = lazy(() => import('../pages/admin/urent/ledger/page'));
const AdminPrepaidPage = lazy(() => import('../pages/admin/prepaid/page'));
const AdminPrepaidLedgerPage = lazy(() => import('../pages/admin/prepaid/ledger/page'));
const AdminPrepaidCustomersListPage = lazy(() => import('../pages/admin/prepaid/customers/page'));
const AdminPrepaidPlansPage = lazy(() => import('../pages/admin/prepaid/plans/page'));
const ApiSettingsPage = lazy(() => import('../pages/admin/settings/api/page'));
const LineNotifySettingsPage = lazy(() => import('../pages/admin/settings/line-notify/page'));
const StorefrontSortSettingsPage = lazy(() => import('../pages/admin/settings/storefront-sort/page'));
const VehiclesPage = lazy(() => import('../pages/admin/vehicles/page'));

const AdminMilestonesPage = lazy(() => import('../pages/admin/milestones/page'));
const AdminServiceCasesPage = lazy(() => import('../pages/admin/service-cases/page'));
const AdminFrontendImagesPage = lazy(() => import('../pages/admin/frontend-images/page'));
const AdminMuViSpaceBookingsPage = lazy(() => import('../pages/admin/muvi-space/bookings/page'));
const AdminMuViSpaceContentPage = lazy(() => import('../pages/admin/muvi-space/content/page'));
const AdminMuViSpaceSettingsPage = lazy(() => import('../pages/admin/muvi-space/settings/page'));

// AI Agent
const AdminAiAgentPage = lazy(() => import('../pages/admin/ai-agent/page'));

const AdminLegalSettingsPage = lazy(() => import('../pages/admin/legal-settings/page'));
const AdminSystemGovernancePage = lazy(() => import('../pages/admin/system-governance/page'));

// Attendance
const LiffAttendancePage = lazy(() => import('../pages/liff/attendance/page'));
const LiffBindPage = lazy(() => import('../pages/liff/bind/page'));
const LiffCustomerLineBindPage = lazy(() => import('../pages/liff/customer-line-bind/page'));
const AdminAttendancePage = lazy(() => import('../pages/admin/attendance/page'));

// Scheduling
const AdminSchedulesPage = lazy(() => import('../pages/admin/schedules/page'));

const routes: RouteObject[] = [
  { path: '/', element: <HomePage /> },
  { path: '/equipment', element: <EquipmentPage /> },
  { path: '/equipment/category/:categoryId', element: <EquipmentCategoryPage /> },
  { path: '/equipment/detail/:equipmentId', element: <EquipmentDetailPage /> },
  { path: '/services', element: <ServicesPage /> },
  { path: '/services/taiwan-japan', element: <TaiwanJapanPage /> },
  { path: '/services/post-production', element: <PostProductionPage /> },
  { path: '/services/urent', element: <UrentPage /> },
  { path: '/locations', element: <LocationsPage /> },
  { path: '/locations/:locationId', element: <LocationDetailPage /> },
  { path: '/about', element: <AboutPage /> },
  { path: '/contact', element: <ContactPage /> },

  // Legal routes
  { path: '/privacy', element: <PrivacyPage /> },
  { path: '/terms', element: <TermsPage /> },
  { path: '/line-privacy', element: <LinePrivacyPage /> },

  // MuVi Space public routes
  { path: '/muvi-space', element: <MuViSpacePage /> },
  { path: '/muvi-space/book', element: <MuViSpaceBookPage /> },

  // Account routes
  { path: '/account/login', element: <LoginPage /> },
  { path: '/account/register', element: <RegisterPage /> },
  { path: '/account/confirm-email', element: <ConfirmEmailPage /> },
  { path: '/account/auth/callback', element: <AuthCallbackPage /> },
  { path: '/account/forgot-password', element: <ForgotPasswordPage /> },
  { path: '/account/reset-password', element: <ResetPasswordPage /> },
  { path: '/account/profile', element: <AuthGuard><ProfilePage /></AuthGuard> },
  { path: '/account/orders', element: <AuthGuard><OrdersPage /></AuthGuard> },
  { path: '/account/orders/:orderId', element: <AuthGuard><OrderDetailPage /></AuthGuard> },
  { path: '/account/notifications', element: <AuthGuard><AccountNotificationsPage /></AuthGuard> },
  { path: '/account/cart', element: <AuthGuard><CartPage /></AuthGuard> },
  { path: '/account/checkout', element: <AuthGuard><CheckoutPage /></AuthGuard> },
  { path: '/account/checkout/success', element: <AuthGuard><CheckoutSuccessPage /></AuthGuard> },
  { path: '/account/prepaid/apply', element: <AuthGuard><PrepaidApplyPage /></AuthGuard> },
  { path: '/account/prepaid/status', element: <AuthGuard><PrepaidStatusPage /></AuthGuard> },
  { path: '/account/prepaid/ledger', element: <AuthGuard><PrepaidLedgerPage /></AuthGuard> },
  { path: '/account/urent/apply', element: <AuthGuard><UrentApplyPage /></AuthGuard> },
  { path: '/account/urent/status', element: <AuthGuard><UrentStatusPage /></AuthGuard> },
  { path: '/account/identity', element: <AuthGuard><IdentityPage /></AuthGuard> },

  // Admin authentication routes
  { path: '/admin/login', element: <AdminLoginPage /> },
  /**
   * @deprecated /admin/register — 已停用，自動重定向至 /admin/login。
   * 管理員建立唯一合法入口為 Invitation Flow (/admin/invite)。
   * 此路由保留僅作向下相容，禁止再對外公開。
   */
  { path: '/admin/register', element: <AdminRegisterPage /> },
  { path: '/admin/invite', element: <AdminInvitePage /> },

  // Admin protected routes - 全部改用 AdminAuthGuard（含 role check）
  { path: '/admin/dashboard', element: <AdminAuthGuard><AdminDashboardPage /></AdminAuthGuard> },
  { path: '/admin/equipment', element: <AdminAuthGuard><AdminEquipmentPage /></AdminAuthGuard> },
  { path: '/admin/equipment/items', element: <AdminAuthGuard><AdminEquipmentItemsPage /></AdminAuthGuard> },
  { path: '/admin/orders', element: <AdminAuthGuard><AdminOrdersPage /></AdminAuthGuard> },
  { path: '/admin/orders/:orderId', element: <AdminAuthGuard><AdminOrderDetailPage /></AdminAuthGuard> },
  { path: '/admin/orders/:orderId/contract', element: <AdminAuthGuard><AdminOrderContractPage /></AdminAuthGuard> },
  { path: '/admin/locations', element: <AdminAuthGuard><AdminLocationsPage /></AdminAuthGuard> },
  { path: '/admin/forms', element: <AdminAuthGuard><AdminFormsPage /></AdminAuthGuard> },
  { path: '/admin/cms', element: <AdminAuthGuard><AdminCMSPage /></AdminAuthGuard> },
  { path: '/admin/analytics', element: <AdminAuthGuard><AdminAnalyticsPage /></AdminAuthGuard> },
  { path: '/admin/contracts', element: <AdminAuthGuard><AdminContractsPage /></AdminAuthGuard> },
  { path: '/admin/urent-contracts', element: <AdminAuthGuard><AdminUrentContractsPage /></AdminAuthGuard> },
  { path: '/admin/settings/google-calendar', element: <AdminAuthGuard><GoogleCalendarSettingsPage /></AdminAuthGuard> },
  { path: '/admin/settings/google-calendar/callback', element: <GoogleCalendarCallbackPage /> },
  { path: '/admin/settings/api', element: <AdminAuthGuard><ApiSettingsPage /></AdminAuthGuard> },
  { path: '/admin/settings/line-notify', element: <AdminAuthGuard><LineNotifySettingsPage /></AdminAuthGuard> },
  { path: '/admin/settings/storefront-sort', element: <AdminAuthGuard><StorefrontSortSettingsPage /></AdminAuthGuard> },

  { path: '/admin/legal-settings', element: <AdminAuthGuard><AdminLegalSettingsPage /></AdminAuthGuard> },
  { path: '/admin/frontend-images', element: <AdminAuthGuard><AdminFrontendImagesPage /></AdminAuthGuard> },
  { path: '/admin/system-governance', element: <AdminAuthGuard><AdminSystemGovernancePage /></AdminAuthGuard> },

  // Admin routes
  {
    path: '/admin',
    children: [
      { path: 'categories', element: <AdminAuthGuard><AdminCategoriesPage /></AdminAuthGuard> },
      { path: 'brands', element: <AdminAuthGuard><BrandsPage /></AdminAuthGuard> },
      { path: 'customers', element: <AdminAuthGuard><AdminCustomersPage /></AdminAuthGuard> },
      { path: 'admins', element: <AdminAuthGuard><AdminAdminsPage /></AdminAuthGuard> },
      { path: 'accessories', element: <AdminAuthGuard><AdminAccessoriesPage /></AdminAuthGuard> },
      { path: 'urent', element: <AdminAuthGuard><AdminUrentManagementPage /></AdminAuthGuard> },
      { path: 'urent/ledger', element: <AdminAuthGuard><AdminUrentLedgerPage /></AdminAuthGuard> },
      { path: 'prepaid', element: <AdminAuthGuard><AdminPrepaidPage /></AdminAuthGuard> },
      { path: 'prepaid/plans', element: <AdminAuthGuard><AdminPrepaidPlansPage /></AdminAuthGuard> },
      { path: 'prepaid/ledger', element: <AdminAuthGuard><AdminPrepaidLedgerPage /></AdminAuthGuard> },
      { path: 'prepaid/customers', element: <AdminAuthGuard><AdminPrepaidCustomersListPage /></AdminAuthGuard> },
      { path: 'vehicles', element: <AdminAuthGuard><VehiclesPage /></AdminAuthGuard> },
      { path: 'locations', element: <AdminAuthGuard><AdminLocationsPage /></AdminAuthGuard> },
      { path: 'attendance', element: <AdminAuthGuard><AdminAttendancePage /></AdminAuthGuard> },
      { path: 'schedules', element: <AdminAuthGuard><AdminSchedulesPage /></AdminAuthGuard> },
      { path: 'ai-agent', element: <AdminAuthGuard><AdminAiAgentPage /></AdminAuthGuard> },
      { path: 'calendar-test', element: <AdminAuthGuard><AdminCalendarTestPage /></AdminAuthGuard> },
      { path: 'milestones', element: <AdminAuthGuard><AdminMilestonesPage /></AdminAuthGuard> },
      { path: 'service-cases', element: <AdminAuthGuard><AdminServiceCasesPage /></AdminAuthGuard> },
      { path: 'muvi-space/bookings', element: <AdminAuthGuard><AdminMuViSpaceBookingsPage /></AdminAuthGuard> },
      { path: 'muvi-space/content', element: <AdminAuthGuard><AdminMuViSpaceContentPage /></AdminAuthGuard> },
      { path: 'muvi-space/settings', element: <AdminAuthGuard><AdminMuViSpaceSettingsPage /></AdminAuthGuard> },
    ],
  },

  // LIFF standalone pages (no navbar / footer)
  { path: '/liff/attendance', element: <LiffAttendancePage /> },
  { path: '/liff/history', element: <LiffAttendancePage /> },
  { path: '/liff/bind', element: <LiffBindPage /> },
  { path: '/liff/customer-line-bind', element: <LiffCustomerLineBindPage /> },

  // Catch‑all 404
  { path: '*', element: <NotFoundPage /> },
];

export default routes;
